import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api, { errorMessage } from "../lib/api";
import { PageHeader, PillBadge, GhostButton } from "../components/Primitives";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import { useFullscreenRoute } from "../components/StickyFiltersBar";
import { toast } from "sonner";
import { MapPin, Layers, Maximize2, Minimize2, Radio } from "lucide-react";
import { formatAddress } from "../lib/address";
import { NewDispatchModal } from "../components/dispatch/NewDispatchModal";

// Deterministic palette — one color per client, cycled from a base of 12.
// Values chosen to stay legible on the dark tile layer.
const CLIENT_PALETTE = [
  "#FF3B30", "#FF9F0A", "#34C759", "#0A84FF", "#BF5AF2", "#5E5CE6",
  "#FF375F", "#66D4CF", "#FFD60A", "#5AC8FA", "#AF52DE", "#FF6482",
];
function colorForClient(id, i) {
  if (!id) return "#71717A";
  return CLIENT_PALETTE[i % CLIENT_PALETTE.length];
}

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "on-hold", label: "On Hold" },
  { value: "inactive", label: "Inactive" },
  { value: "ended", label: "Ended" },
];

// Auto-recenter/zoom so all visible pins fit into view.
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 12);
      return;
    }
    const bounds = points.map((p) => [p.lat, p.lng]);
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
  }, [points, map]);
  return null;
}

export default function SitesMap() {
  const { pathname } = useLocation();
  const isFs = useFullscreenRoute(pathname);
  const onFs = () => (isFs ? window.close() : window.open("/sites/map/fullscreen", "_blank", "noopener"));
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tileLight, setTileLight] = useState(true);

  // Filters — bidirectional narrowing pattern from Sites page.
  const [clientFilter, setClientFilter] = useState([]);
  const [siteFilter, setSiteFilter] = useState([]);
  const [cityFilter, setCityFilter] = useState([]);
  const [stateFilter, setStateFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState(["active"]);
  // Site the user wants to dispatch to (via popover Dispatch button). When
  // set, we render <NewDispatchModal restrictToSite={...}> which auto-limits
  // the blast to guards currently on-duty at that site.
  const [dispatchForSite, setDispatchForSite] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([api.get("/sites"), api.get("/clients")]);
      setSites(s.data || []);
      setClients(c.data || []);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load"));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Assign a stable color per client_id (ordering by first occurrence).
  const clientColorMap = useMemo(() => {
    const map = new Map();
    let i = 0;
    for (const c of clients) {
      if (!map.has(c.id)) { map.set(c.id, colorForClient(c.id, i)); i++; }
    }
    return map;
  }, [clients]);

  const scopedSites = useMemo(() => {
    const cf = new Set(clientFilter), sf = new Set(siteFilter);
    const ctf = new Set(cityFilter), stf = new Set(stateFilter);
    const stat = new Set(statusFilter);
    return sites.filter((s) => {
      if (cf.size && !cf.has(s.client_id)) return false;
      if (sf.size && !sf.has(s.id)) return false;
      if (ctf.size && !ctf.has((s.city || "").trim())) return false;
      if (stf.size && !stf.has((s.state || "").trim())) return false;
      if (stat.size && !stat.has(s.status || "active")) return false;
      return true;
    });
  }, [sites, clientFilter, siteFilter, cityFilter, stateFilter, statusFilter]);

  const plotted = useMemo(
    () => scopedSites
      .filter((s) => s.lat != null && s.lng != null && !Number.isNaN(Number(s.lat)))
      .map((s) => ({ ...s, lat: Number(s.lat), lng: Number(s.lng) })),
    [scopedSites]
  );

  // Bidirectional option pools — narrow other dimensions based on selections.
  const clientOptions = useMemo(() => {
    const allowed = new Set(sites
      .filter((s) => (!siteFilter.length || siteFilter.includes(s.id))
                  && (!cityFilter.length || cityFilter.includes((s.city || "").trim()))
                  && (!stateFilter.length || stateFilter.includes((s.state || "").trim()))
                  && (!statusFilter.length || statusFilter.includes(s.status || "active")))
      .map((s) => s.client_id).filter(Boolean));
    return clients
      .filter((c) => !allowed.size || allowed.has(c.id))
      .map((c) => ({ value: c.id, label: c.company_name, sub: formatAddress(c) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sites, clients, siteFilter, cityFilter, stateFilter, statusFilter]);

  const siteOptions = useMemo(() => sites
    .filter((s) => (!clientFilter.length || clientFilter.includes(s.client_id))
                && (!cityFilter.length || cityFilter.includes((s.city || "").trim()))
                && (!stateFilter.length || stateFilter.includes((s.state || "").trim()))
                && (!statusFilter.length || statusFilter.includes(s.status || "active")))
    .map((s) => ({ value: s.id, label: s.name, sub: s.client_name }))
    .sort((a, b) => a.label.localeCompare(b.label)),
  [sites, clientFilter, cityFilter, stateFilter, statusFilter]);

  const cityOptions = useMemo(() => Array.from(new Set(sites
    .filter((s) => (!clientFilter.length || clientFilter.includes(s.client_id))
                && (!siteFilter.length || siteFilter.includes(s.id))
                && (!stateFilter.length || stateFilter.includes((s.state || "").trim()))
                && (!statusFilter.length || statusFilter.includes(s.status || "active")))
    .map((s) => (s.city || "").trim()).filter(Boolean)))
    .sort().map((c) => ({ value: c, label: c })),
  [sites, clientFilter, siteFilter, stateFilter, statusFilter]);

  const stateOptions = useMemo(() => Array.from(new Set(sites
    .filter((s) => (!clientFilter.length || clientFilter.includes(s.client_id))
                && (!siteFilter.length || siteFilter.includes(s.id))
                && (!cityFilter.length || cityFilter.includes((s.city || "").trim()))
                && (!statusFilter.length || statusFilter.includes(s.status || "active")))
    .map((s) => (s.state || "").trim()).filter(Boolean)))
    .sort().map((c) => ({ value: c, label: c })),
  [sites, clientFilter, siteFilter, cityFilter, statusFilter]);

  // Legend: unique clients appearing on the current map view, with colors.
  const legend = useMemo(() => {
    const seen = new Set();
    const rows = [];
    for (const s of plotted) {
      if (s.client_id && !seen.has(s.client_id)) {
        seen.add(s.client_id);
        rows.push({
          id: s.client_id,
          name: s.client_name,
          color: clientColorMap.get(s.client_id) || "#71717A",
          count: 0,
        });
      }
    }
    // count sites per client
    for (const s of plotted) {
      const row = rows.find((r) => r.id === s.client_id);
      if (row) row.count += 1;
    }
    return rows.sort((a, b) => b.count - a.count);
  }, [plotted, clientColorMap]);

  const clearAll = () => {
    setClientFilter([]); setSiteFilter([]); setCityFilter([]); setStateFilter([]); setStatusFilter([]);
  };
  const anyFilter = clientFilter.length || siteFilter.length || cityFilter.length || stateFilter.length || statusFilter.length !== 1 || statusFilter[0] !== "active";

  return (
    <div>
      <PageHeader
        title="Sites Map"
        subtitle="Geographic overview of every site. Filter by client, site, city, state, or status. Markers colored by client."
        action={
          <div className="flex items-center gap-2">
            {anyFilter && <GhostButton data-testid="sites-map-clear" onClick={clearAll}>Clear filters</GhostButton>}
            <GhostButton
              data-testid="sites-map-toggle-tile"
              onClick={() => setTileLight((v) => !v)}
              title="Toggle basemap"
            >
              <Layers className="size-3.5" /> {tileLight ? "Light" : "Dark"}
            </GhostButton>
            <GhostButton
              data-testid={isFs ? "sites-map-exit-fullscreen" : "sites-map-fullscreen"}
              onClick={onFs}
              title={isFs ? "Close fullscreen" : "Open in fullscreen"}
            >
              {isFs ? <><Minimize2 className="size-3.5" /> Close</> : <><Maximize2 className="size-3.5" /> Fullscreen</>}
            </GhostButton>
          </div>
        }
      />
      <div className="p-8 space-y-4">
        {/* iter298 — Leaflet paints its tile/marker/popup layers at
            z-index 400–1000. The filter-bar dropdown popovers used to
            render at default stacking context, so opening a filter over
            the map hid the options behind the tiles. Anchor the whole
            row at z-[1100] (>Leaflet controls) so every popover it
            renders wins over the map layer. */}
        <div className="relative z-[1100] grid grid-cols-2 md:grid-cols-5 gap-3">
          <MultiSelectDropdown testId="sites-map-client" label="All clients" options={clientOptions}
            selected={clientFilter} onChange={setClientFilter} searchable triggerClassName="w-full" />
          <MultiSelectDropdown testId="sites-map-site" label="All sites" options={siteOptions}
            selected={siteFilter} onChange={setSiteFilter} searchable triggerClassName="w-full" />
          <MultiSelectDropdown testId="sites-map-city" label="All cities" options={cityOptions}
            selected={cityFilter} onChange={setCityFilter} searchable triggerClassName="w-full" />
          <MultiSelectDropdown testId="sites-map-state" label="All states" options={stateOptions}
            selected={stateFilter} onChange={setStateFilter} searchable triggerClassName="w-full" />
          <MultiSelectDropdown testId="sites-map-status" label="All statuses" options={STATUS_OPTIONS}
            selected={statusFilter} onChange={setStatusFilter} triggerClassName="w-full" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Map */}
          <div className="lg:col-span-3 border border-[#27272A] bg-[#121212] p-2" data-testid="sites-map-container">
            <div style={{ height: "560px", width: "100%" }}>
              <MapContainer center={[39.8283, -98.5795]} zoom={4} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
                {tileLight ? (
                  <TileLayer attribution='&copy; OSM' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                ) : (
                  <TileLayer attribution='&copy; Carto' url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                )}
                <FitBounds points={plotted} />
                {plotted.map((s) => {
                  const color = clientColorMap.get(s.client_id) || "#71717A";
                  return (
                    <CircleMarker
                      key={s.id}
                      center={[s.lat, s.lng]}
                      radius={9}
                      pathOptions={{ color, fillColor: color, fillOpacity: 0.75, weight: 2 }}
                    >
                      <Popup>
                        <div className="text-xs">
                          <div className="font-bold text-sm mb-1">{s.name}</div>
                          <div className="text-zinc-600 mb-1">{s.client_name}</div>
                          <div className="text-zinc-700">{s.address || formatAddress(s)}</div>
                          <div className="mt-2 flex items-center gap-1 text-[10px] uppercase tracking-wider">
                            <PillBadge tone={s.status === "active" ? "green" : "amber"}>{s.status || "active"}</PillBadge>
                            <span className="text-zinc-500">· {s.guards_required || 1} guard(s)</span>
                          </div>
                          <div className="mt-2 flex items-center gap-3">
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`}
                              target="_blank" rel="noopener noreferrer"
                              data-testid={`sites-map-directions-${s.id}`}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#0A84FF] hover:underline"
                            >
                              → Directions
                            </a>
                            <button
                              type="button"
                              onClick={() => setDispatchForSite({ id: s.id, name: s.name })}
                              data-testid={`sites-map-dispatch-${s.id}`}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#0A84FF] hover:underline"
                            >
                              <Radio className="size-3" /> Dispatch on-duty
                            </button>
                          </div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>
            </div>
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-between">
              <span data-testid="sites-map-count">
                {loading ? "Loading…" : `${plotted.length} plotted / ${scopedSites.length} matched / ${sites.length} total`}
              </span>
              {scopedSites.length > plotted.length && (
                <span className="text-[#FF9F0A]">
                  ⚠ {scopedSites.length - plotted.length} matched site(s) missing coordinates
                </span>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="border border-[#27272A] bg-[#121212]" data-testid="sites-map-legend">
            <div className="px-4 py-3 border-b border-[#27272A]">
              <div className="label-mute mb-1">Legend</div>
              <div className="font-display uppercase font-bold text-sm">Clients on map</div>
              <div className="text-[10px] text-zinc-500 mt-1">{legend.length} client{legend.length === 1 ? "" : "s"}</div>
            </div>
            <div className="max-h-[500px] overflow-y-auto divide-y divide-[#1c1c1e]">
              {legend.length === 0 && (
                <div className="p-6 text-center text-xs text-zinc-500 italic">No sites plotted for the selected filters.</div>
              )}
              {legend.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  data-testid={`sites-map-legend-${row.id}`}
                  onClick={() => setClientFilter([row.id])}
                  className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-[#1c1c1e]"
                >
                  <span className="inline-block size-3 rounded-full shrink-0" style={{ background: row.color }} />
                  <span className="flex-1 min-w-0 truncate text-sm">{row.name}</span>
                  <span className="font-mono text-xs text-zinc-500">{row.count}</span>
                </button>
              ))}
            </div>
            {legend.length > 0 && (
              <div className="p-3 border-t border-[#27272A]">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                  <MapPin className="size-3" /> Click a client to filter
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {dispatchForSite && (
        <NewDispatchModal
          restrictToSite={dispatchForSite}
          onClose={() => setDispatchForSite(null)}
          onSent={() => setDispatchForSite(null)}
        />
      )}
    </div>
  );
}
